import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Scrollbars } from 'react-custom-scrollbars-2';
import { Library, ChevronLeft, ChevronRight, Menu, X,  } from 'lucide-react';
import styles from './BookPreview.module.css';
import scrollStyles from './BookPreview.module.css';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useChaptersBySeriesId } from '../../hooks/useChapters';

function BookPreview() {
  const [showLibrary, setShowLibrary] = useState(false);
  const [currentChapterIndex, setCurrentChapterIndex] = useState(0);
  const [animating, setAnimating] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [initialClickOffset, setInitialClickOffset] = useState(0);
  
  const scrollbarRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const circleRef = useRef(null);
  
  // Update scroll position when content is scrolled
  const handleContentScroll = ({ top }) => {
    if (!isDragging && scrollContainerRef.current) {
      const containerHeight = scrollContainerRef.current.clientHeight;
      const circleHeight = 16;
      const maxPosition = containerHeight - circleHeight;
      setScrollPosition(top * maxPosition);
    }
  };

  // Handle mouse down on the circle
  const handleMouseDown = (e) => {
    e.preventDefault();
    
    if (circleRef.current && scrollContainerRef.current) {
      const circleRect = circleRef.current.getBoundingClientRect();
      
      // Calculate the offset of the click within the circle
      // This ensures we grab the circle exactly where the user clicked
      const offsetY = e.clientY - circleRect.top;
      setInitialClickOffset(offsetY);
      
      setIsDragging(true);
    }
  };

  // Handle mouse move (dragging)
  const handleMouseMove = useCallback((e) => {
    if (isDragging && scrollContainerRef.current && scrollbarRef.current) {
      const containerRect = scrollContainerRef.current.getBoundingClientRect();
      const circleHeight = 16;
      
      // Calculate new position, accounting for the initial click offset
      // within the circle to make dragging feel natural
      let newY = e.clientY - containerRect.top - initialClickOffset;
      
      const maxY = containerRect.height - circleHeight;
      
      // Constrain within bounds
      newY = Math.max(0, Math.min(newY, maxY));
      
      // Update circle position
      setScrollPosition(newY);
      
      // Calculate scroll percentage and update content scroll position
      const scrollPercentage = newY / maxY;
      if (scrollbarRef.current) {
        scrollbarRef.current.scrollTop(scrollPercentage * scrollbarRef.current.getScrollHeight());
      }
    }
  }, [isDragging, initialClickOffset]);

  // Handle mouse up
  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);



  // Add/remove event listeners for mouse move and up
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    }
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Get route parameters
  const { id, slug } = useParams();
  const location = useLocation();
  // Extract seriesId from location state, or use id as fallback
  const seriesId = location.state?.seriesId || id;
  const navigate = useNavigate();
  
  // Get series data from hook
  const { series, chapters } = useChaptersBySeriesId(seriesId);
  
  
  // Helper function to format title for URL
  const formatTitleForUrl = useCallback((title) => {
    if (!title) return '';
    
    // Use encodeURIComponent for non-Latin scripts (like Hindi)
    // but still try to make clean URLs for Latin characters
    if (/[^\u0000-\u007F]/.test(title)) {
      // Contains non-ASCII characters, use the slug directly if available
      return title;
    }
    
    // For Latin characters, create a clean slug
    return title
      .toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-');
  }, []);
  
  // Define navigation functions with useCallback to avoid dependency issues
  const navigateBack = useCallback(() => {
    navigate(`/book-details/${chapters[0].ebook_slug}`);
  }, [navigate, chapters]);

  const nextChapter = useCallback(() => {
    if (!chapters || currentChapterIndex >= chapters.length - 1 || animating) return;
    
    setAnimating(true);
    setTimeout(() => {
      const nextChapterTitle = chapters[currentChapterIndex + 1].ebook_title;
      const formattedTitle = formatTitleForUrl(nextChapterTitle);
      
      navigate(`/book/${id}/episode/${formattedTitle}`, { 
        state: { seriesId } 
      });
      setCurrentChapterIndex(currentChapterIndex + 1);
      setAnimating(false);
    }, 500);
  }, [chapters, currentChapterIndex, animating, navigate, id, seriesId, formatTitleForUrl]);

  const prevChapter = useCallback(() => {
    if (!chapters || currentChapterIndex <= 0 || animating) return;
    
    setAnimating(true);
    setTimeout(() => {
      const prevChapterTitle = chapters[currentChapterIndex - 1].ebook_title;
      const formattedTitle = formatTitleForUrl(prevChapterTitle);
      
      navigate(`/book/${id}/episode/${formattedTitle}`, { 
        state: { seriesId } 
      });
      setCurrentChapterIndex(currentChapterIndex - 1);
      setAnimating(false);
    }, 500);
  }, [chapters, currentChapterIndex, animating, navigate, id, seriesId, formatTitleForUrl]);

  const selectChapter = useCallback((index) => {
    if (!chapters || index === currentChapterIndex || animating) return;
    
    setAnimating(true);
    setTimeout(() => {
      const selectedChapterTitle = chapters[index].ebook_title;
      const formattedTitle = formatTitleForUrl(selectedChapterTitle);
      
      navigate(`/book/${id}/episode/${formattedTitle}`, { 
        state: { seriesId } 
      });
      setCurrentChapterIndex(index);
      setAnimating(false);
      if (window.innerWidth < 768) {
        setShowLibrary(false);
      }
    }, 500);
  }, [chapters, currentChapterIndex, animating, navigate, id, seriesId, formatTitleForUrl]);

  // Toggle library sidebar
  const toggleLibrary = useCallback(() => {
    setShowLibrary(prev => !prev);
  }, []);

  // Find the current chapter index based on the URL parameter (now using formatted title)
  useEffect(() => {
    if (chapters && chapters.length > 0 && slug) {
      // First try direct match with the slug from the URL
      const slugIndex = chapters.findIndex(chapter => chapter.ebook_slug === slug);
      
      if (slugIndex !== -1) {
        setCurrentChapterIndex(slugIndex);
      } else {
        // If direct slug match fails, try matching by formatted title
        const titleIndex = chapters.findIndex(chapter => {
          const formattedTitle = formatTitleForUrl(chapter.ebook_title);
          return formattedTitle === slug;
        });
        
        if (titleIndex !== -1) {
          setCurrentChapterIndex(titleIndex);
        } else {
          // If all else fails, default to first chapter
          setCurrentChapterIndex(0);
        }
      }
    }
  }, [chapters, slug, formatTitleForUrl]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight') {
        nextChapter();
      } else if (e.key === 'ArrowLeft') {
        prevChapter();
      } else if (e.key === 'Escape') {
        navigateBack();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [nextChapter, prevChapter, navigateBack]);

  // Format content to handle HTML
  const formatContent = (content) => {
    return { __html: content };
  };

  // Loading state
  if (!chapters || chapters.length === 0 || !series) {
    return <div className={styles.loading}>Loading book content...</div>;
  }

  // Make sure we have a valid currentChapterIndex
  const validIndex = Math.min(Math.max(0, currentChapterIndex), chapters.length - 1);
  const currentChapter = chapters[validIndex];
  
  if (!currentChapter) {
    return <div className={styles.loading}>Chapter not found</div>;
  }

  return (
    <>
    <div className={styles.bookContainer}>
      {/* Library sidebar */}
      <div className={`${styles.librarySidebar} ${showLibrary ? styles.active : ''}`}>
        <div className={styles.readerHeader}>
          <button className={styles.libraryButton} onClick={toggleLibrary}>
            <Library size={24} />
          </button>
          <div className={styles.bookInfo}>
            <h2 className={styles.bookTitle}>{series.title}</h2>
            <p className={styles.chapterTitle}>{currentChapter.ebook_title}</p>
          </div>
          <div className={styles.chapterIndicator}>
            Chapter {currentChapter.chapter_number} of {chapters.length}
          </div>
        </div>
        <div className={styles.chapterList}>
          <h3>{series.title}</h3>
          <p className={styles.author}>by {currentChapter.ebook_author}</p>
            {chapters.map((chapter, index) => (
              <div 
                key={chapter.id} 
                className={`${styles.chapterItem} ${index === validIndex ? styles.active : ''}`}
                onClick={() => selectChapter(index)}
              >
                <span className={styles.chapterNumber}>Chapter {chapter.chapter_number}</span>
                <span className={styles.chapterTitle}>{chapter.ebook_title}</span>
              </div>
            ))}
        </div>
      </div>

      {/* Main reading area */}
      <div className={styles.readerContainer}>
        {/* Top navigation */}
        <div className={styles.readerHeader}>
          <button className={styles.libraryButton} onClick={toggleLibrary}>
            <Library size={24} />
          </button>
          <div className={styles.bookInfo}>
            <h2 className={styles.bookTitle}>{series.title}</h2>
            <p className={styles.chapterTitle}>{currentChapter.ebook_title}</p>
          </div>
          <div className={styles.chapterIndicator}>
            Chapter {currentChapter.chapter_number} of {chapters.length}
          </div>
          <button className={styles.backButton} onClick={navigateBack}>
            <X size={24} />
          </button>
        </div>

        {/* Chapter content */}
        <div className={styles.contentContainer}>
          <button 
            className={`${styles.navButton} ${styles.prevButton} ${validIndex === 0 ? styles.disabled : ''}`} 
            onClick={prevChapter}
            disabled={validIndex === 0}
          >
            <ChevronLeft size={24} />
          </button>
          
          <Scrollbars 
            universal
            className={styles.contentScrollbar}
            ref={scrollbarRef}                         // Attach the ref here
            onScrollFrame={handleContentScroll}        // Update scroll position when content scrolls
            renderThumbVertical={props => <div {...props} className={styles.scrollThumb} />}
          >
            <div 
              key={currentChapter.id} 
              className={`${styles.chapterContent} ${animating ? styles.fadeOut : styles.fadeIn}`}
              style={{ fontFamily: "'Raleway', sans-serif" }}
            >
              <h2 
                className={styles.contentChapterTitle} 
                style={{ fontFamily: "'Cinzel Decorative', serif" }}
              >
                Chapter {currentChapter.chapter_number}: {currentChapter.ebook_title}
              </h2>
              <div className={styles.bookFullContent} dangerouslySetInnerHTML={formatContent(currentChapter.ebook_content)} />
            </div>
          </Scrollbars>
          
          <button 
            className={`${styles.navButton} ${styles.nextButton} ${validIndex === chapters.length - 1 ? styles.disabled : ''}`} 
            onClick={nextChapter}
            disabled={validIndex === chapters.length - 1}
          >
            <ChevronRight size={24} />
          </button>
        </div>
        {/* Custom scroll indicator */}
        <div 
            className={scrollStyles.scrollContainer} 
            ref={scrollContainerRef}
            onClick={(e) => {
              // Handle direct clicks on the scrollbar track
              if (scrollContainerRef.current && scrollbarRef.current) {
                const containerRect = scrollContainerRef.current.getBoundingClientRect();
                const circleHeight = 16;
                
                // Calculate position relative to container
                let newY = e.clientY - containerRect.top - (circleHeight / 2);
                const maxY = containerRect.height - circleHeight;
                
                // Constrain within bounds
                newY = Math.max(0, Math.min(newY, maxY));
                
                // Update circle position
                setScrollPosition(newY);
                
                // Update content scroll position
                const scrollPercentage = newY / maxY;
                scrollbarRef.current.scrollTop(scrollPercentage * scrollbarRef.current.getScrollHeight());
              }
            }}
          >
            <div className={scrollStyles.scrollLine}></div>
            <div 
              ref={circleRef}
              className={scrollStyles.scrollCircle} 
              style={{ top: `${scrollPosition}px` }}
              onMouseDown={handleMouseDown}
            ></div>
          </div>
        {/* Bottom navigation for mobile view */}
        <div className={styles.mobileNavigation}>
          <button 
            className={`${styles.mobileNavButton} ${validIndex === 0 ? styles.disabled : ''}`} 
            onClick={prevChapter}
            disabled={validIndex === 0}
          >
            <ChevronLeft size={18} /> Previous
          </button>
          <button 
            className={styles.mobileMenuButton} 
            onClick={toggleLibrary}
          >
            <Menu size={18} /> Chapters
          </button>
          <button 
            className={`${styles.mobileNavButton} ${validIndex === chapters.length - 1 ? styles.disabled : ''}`} 
            onClick={nextChapter}
            disabled={validIndex === chapters.length - 1}
          >
            Next <ChevronRight size={18} />
          </button>
        </div>
      </div>
    </div>
   </>
  );
}

export default BookPreview;